import Head from 'next/head';
import { useRouter } from 'next/router';

import clsxm from '@/lib/clsxm';

import AgentFaq from '@/components/agents/AgentFaq';
import AgentFinalCta from '@/components/agents/AgentFinalCta';
import AgentLogos from '@/components/agents/AgentLogos';
import AgentReviews from '@/components/agents/AgentReviews';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';

import ChannelHero from './ChannelHero';
import ChannelJobs from './ChannelJobs';
import ChannelLinks from './ChannelLinks';
import ChannelRules from './ChannelRules';
import ChannelSetup from './ChannelSetup';
import ChannelSpotlight from './ChannelSpotlight';
import type { ChannelContent } from './types';
import { useChannelsT } from './useChannelsT';
import { tone } from '../primitives';

const SITE = 'https://easy-peasy.ai';

/*
 * A channel page under /chatbots. Story: the agent answering on this channel
 * (hero), the work it takes over there (jobs), how to connect it (setup), the
 * one thing that sets the channel apart (spotlight), the channel's rules and
 * plan, then proof, questions and the way on to the other channels.
 */
export default function ChannelPage({ content }: { content: ChannelContent }) {
  const { t, lang } = useChannelsT();
  const { locale } = useRouter();
  const prefix = locale && locale !== 'en' ? `/${locale}` : '';

  const breadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('AI Agents'),
        item: `${SITE}${prefix}/chatbots`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: content.hero.label,
        item: `${SITE}${prefix}/chatbots/${content.key}`,
      },
    ],
  };

  return (
    // `relative` makes <main> the containing block of the footer's sr-only
    // heading, so overflow-x-clip catches the 1px it pokes out in RTL.
    <main
      className={clsxm('relative overflow-x-clip', tone.page)}
      dir={lang === 'ar' ? 'rtl' : undefined}
    >
      <Seo
        templateTitle={content.seoTitle}
        description={content.seoDescription}
        image={`${SITE}/api/og-template?title=${encodeURIComponent(
          content.hero.label,
        )}`}
      />
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbs).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <ChannelHero content={content} />
      <AgentLogos />
      <ChannelJobs jobs={content.jobs} />
      <ChannelSetup setup={content.setup} />
      <ChannelSpotlight spotlight={content.spotlight} />
      <ChannelRules rules={content.rules} />
      <AgentReviews />
      <AgentFaq faqs={content.faqs} />
      <ChannelLinks current={content.key} />
      <AgentFinalCta
        title={content.finalTitle}
        exchange={content.finalExchange}
      />
      <Footer />
    </main>
  );
}
